import { ROLE_ASSISTANT, ROLE_SYSTEM, ROLE_USER, ANALYST_TOOLS, TOOL_RUN_AGENT } from "./LLMClient";
import { DEV } from "../lib/configMgr";
import {observer} from "mobx-react";
import { ContentBlock } from "./ContentBlock";
import FileAttachments from "../components/controls/FileAttachments";
import { useState, useRef, useEffect } from "react";

export const Turn = observer(({
                                turn,
                                turnNumber,
                                trapErrors = false,
                                agentId = null,
                                toolUseLookup = null,
                                showAdvancedFeatures = false,
                                timestamp = null,
                                showDetails = null,
                                resolveSourceLinks = null,
                                replayConversation = false,
                                discardConversation = false,
                                busy = false,
                                userStore = null,
                                isStickyPrompt = false,
                                mentionedGenes = null,
                                editable = false,
                                onEdit = null,
                                onSave = null,
                                onCancel = null,
                                onVisibilityChange = null,
                     }) => {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const turnRef = useRef(null);
  const contentRef = useRef(null);
  const justExpandedRef = useRef(false);
  const wasExpandedBeforeEditRef = useRef(false);
  const previousEditableRef = useRef(false);

  // Auto-expand when entering edit mode, and restore when exiting
  // Only do this if the prompt is actually truncated
  useEffect(() => {
    const wasEditing = previousEditableRef.current;
    const isNowEditing = editable;

    if (isStickyPrompt && isTruncated) {
      // Only manage expand/collapse for truncated prompts
      if (isNowEditing && !wasEditing) {
        // Entering edit mode - save current state and expand
        wasExpandedBeforeEditRef.current = expanded;
        if (!expanded) {
          setExpanded(true);
        }
      } else if (!isNowEditing && wasEditing) {
        // Exiting edit mode - restore previous state
        if (wasExpandedBeforeEditRef.current === false) {
          setExpanded(false);
        }
      }
    }

    previousEditableRef.current = editable;
  }, [editable, isStickyPrompt, isTruncated, expanded]);

  // Check if content is actually truncated - run once on mount and when turn changes
  useEffect(() => {
    if (isStickyPrompt && contentRef.current) {
      // Force a layout calculation by temporarily applying the collapsed styles
      const element = contentRef.current.querySelector('.content-block-text');
      if (element) {
        const originalDisplay = element.style.display;
        const originalWebkitLineClamp = element.style.webkitLineClamp;
        const originalWebkitBoxOrient = element.style.webkitBoxOrient;
        const originalOverflow = element.style.overflow;

        // Temporarily apply the collapsed style to measure
        element.style.display = '-webkit-box';
        element.style.webkitLineClamp = '2';
        element.style.webkitBoxOrient = 'vertical';
        element.style.overflow = 'hidden';

        // Check if the content is taller than the clamped height
        // Add 2px tolerance for rounding/rendering differences
        const truncated = element.scrollHeight > element.clientHeight + 2;
        setIsTruncated(truncated);

        // Restore original styles
        element.style.display = originalDisplay;
        element.style.webkitLineClamp = originalWebkitLineClamp;
        element.style.webkitBoxOrient = originalWebkitBoxOrient;
        element.style.overflow = originalOverflow;
      }
    }
  }, [isStickyPrompt, turn]);


  // Handle auto-collapse when expanded prompt scrolls up (user scrolling down)
  useEffect(() => {
    if (!isStickyPrompt || !expanded || !turnRef.current) {
      return;
    }

    const turnElement = turnRef.current;
    const scrollContainer = turnElement.closest('.conversation-scroller');
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!turnElement) return;

      // Don't auto-collapse if user just clicked expand or if editing
      if (justExpandedRef.current || editable) return;

      const rect = turnElement.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Collapsed height is approximately 2 lines + controls (around 96px with padding)
      const collapsedHeight = 96;

      // When expanded, the prompt has position: relative (not sticky) so it scrolls normally.
      // Auto-collapse when scrolling causes only the collapsed portion to remain visible,
      // which means the bottom of the expanded prompt is at or above where the collapsed bottom would be.

      // Only auto-collapse if the prompt has scrolled up past the container top
      // AND only the collapsed portion height remains visible
      if (rect.top < containerRect.top && rect.bottom <= containerRect.top + collapsedHeight) {
        setExpanded(false);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isStickyPrompt, expanded, editable]);

  // Handle expand/collapse toggle
  const handleToggleExpand = () => {
    if (!expanded) {
      // User is expanding - set flag to prevent immediate auto-collapse
      justExpandedRef.current = true;

      const scrollContainer = turnRef.current?.closest('.conversation-scroller');

      if (scrollContainer && turnRef.current) {
        // Check if this prompt is currently stuck at the top (sticky)
        const containerRect = scrollContainer.getBoundingClientRect();
        const promptRect = turnRef.current.getBoundingClientRect();
        const isCurrentlySticky = Math.abs(promptRect.top - containerRect.top) < 5;

        if (isCurrentlySticky) {
          // Prompt is sticky at top - expand and scroll to keep it at top
          setExpanded(true);

          // Disable autoscroll while we manipulate scrollTop directly
          scrollContainer.classList.add('disable-autoscroll');

          // After state update, ensure the element stays at the top
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (turnRef.current && scrollContainer) {
                // Scroll so the expanded element is at the top of the viewport
                scrollContainer.scrollTop = turnRef.current.offsetTop;
              }
            });
          });

          // Re-enable autoscroll after expansion completes
          setTimeout(() => {
            scrollContainer.classList.remove('disable-autoscroll');
          }, 300);
        } else {
          // Prompt is not sticky - expand in place without scrolling
          // Block autoscroll by adding disable-autoscroll class (checked by useAutoScroll hook)
          scrollContainer.classList.add('disable-autoscroll');

          // Expand the prompt
          setExpanded(true);

          // Keep the class for a bit to prevent autoscroll after expansion completes
          setTimeout(() => {
            scrollContainer.classList.remove('disable-autoscroll');
          }, 300);
        }
      } else {
        setExpanded(true);
      }

      // Clear flag after a delay to allow scrolling and re-layout
      setTimeout(() => {
        justExpandedRef.current = false;
      }, 500);
    } else {
      // Collapsing - scroll to make this prompt sticky at the top
      if (turnRef.current) {
        const scrollContainer = turnRef.current.closest('.conversation-scroller');
        if (scrollContainer) {
          // Collapse the prompt first
          setExpanded(false);

          // Disable autoscroll while we manipulate scrollTop directly
          scrollContainer.classList.add('disable-autoscroll');

          // After collapse, scroll so this prompt is at the top and becomes sticky
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (turnRef.current && scrollContainer) {
                // Scroll to position this prompt at the top of the viewport
                scrollContainer.scrollTop = turnRef.current.offsetTop;
              }
            });
          });

          // Re-enable autoscroll after collapse completes
          setTimeout(() => {
            scrollContainer.classList.remove('disable-autoscroll');
          }, 300);
        } else {
          setExpanded(false);
        }
      } else {
        setExpanded(false);
      }
    }
  };

  const isTop = agentId && agentId.indexOf("tool") === -1;
  const {
    role = ROLE_USER,
    content: rawContent = [],
    usage: {
      inputTokens = null,
      outputTokens = null,
      totalTokens = null
    } = {},
    agent = null,
    ts = null,
    error = null,
    partial = false,
    _progress: searchProgress = undefined,
    _lastInfo: searchLastInfo = undefined,
  } = turn;
  const isSearchProgress = searchProgress != null;
  if (rawContent.some(b => !b)) {
    const context = agentId ? ` (${agentId})` : "";
    DEV && console.warn(`Empty content blocks found in turn ${turnNumber}${context}`);
  }
  // On system messages, use only the final content block (i.e. status)
  const content = (role === ROLE_SYSTEM ? [rawContent.at(-1)] : rawContent).filter(b => b);
  const stopReason = turn.stop_reason || turn.stopReason || "";
  const tokenUsage = showAdvancedFeatures && inputTokens != null ? ` ${inputTokens}/${outputTokens}/${totalTokens}` : "";
  const errorClass = stopReason.indexOf("error") !== -1 || turn.status === "error" || turn.error ? " alert alert-error" : "";
  const partialClass = partial ? " partial-message" : "";
  // Apply sticky-prompt class to all eligible prompts
  // Expanded prompts get the 'expanded' class which makes them non-sticky
  const stickyClass = isStickyPrompt
    ? (expanded ? " sticky-prompt expanded" : " sticky-prompt")
    : "";
  const toolClass = content?.some(b => b.toolResult)
                    ? " has-tool-result"
                    : content?.some(b => b.toolUse)
                      ? " has-tool-use"
                      : role === ROLE_USER && isTop
                        ? " prompt" : "";
  // Consolidate all attachments
  const fileBlocks = content
    .filter(block => block.document || block.image)
    .map(block => block.document || block.image);
  const existingAttachmentFiles = fileBlocks.map(block => ({
    file: {...block.source.file, name: block.source.file?.name || block.name},
    error: block.error,
    errorMessage: block.errorMessage,
    uploadId: block.source.file?.uploadId
  }));
  const attachments = [];
  if (fileBlocks.length && !editable) {
    attachments.push(<FileAttachments key="attachments" value={existingAttachmentFiles} readOnly={true} />);
  }
  const hasToolUse = content.some(block => block.toolUse);
  if (turn.status === "error" || turn.error || turn.stop_reason === "error") {
    const isAdmin = userStore?.isAdmin || false;
    // Hide content-filter stops in tool sub-agent turns — the guide surfaces the
    // outcome in its own response. For root-agent turns (isTop), show the error.
    const SILENT_STOPS = ["guardrail_intervened"];
    const isToolSubAgent = agentId && agentId.indexOf("tool") !== -1;
    if (SILENT_STOPS.includes(turn.stopReason)) return null;
    if (turn.stopReason === "content_filtered" && isToolSubAgent) return null;
    // Collect user-facing text and any admin-only detail from content blocks
    const userText = content.filter(b => b.text).map(b => b.text).join("\n")
                     || "An error occurred. Please try again.";
    const adminDetails = content.map(b => b.admin_detail).filter(Boolean);
    return (
      <div className={`turn turn-${turnNumber} ${role}${toolClass}${errorClass}${partialClass}${stickyClass}`}
           title={isAdmin ? JSON.stringify(turn) : undefined}
      >
        <span>{userText}</span>
        {isAdmin && adminDetails.length > 0 && (
          <pre className="error-admin-detail">{adminDetails.join("\n")}</pre>
        )}
      </div>
    )
  }
  // Check if turn has any visible content
  const isAdmin = userStore?.isAdmin || false;
  // A streamed (generated) image lacks source.file, unlike a user-uploaded attachment —
  // only the latter is excluded here (ContentBlock routes it to FileAttachments separately).
  const nonFileContent = content.filter(block => !(block.document || (block.image && block.image.source?.file)));
  const hasVisibleContent = nonFileContent.some(block => {
    // Text content is always visible
    if (block.text) return true;
    // Tool results are visible
    if (block.toolResult) return true;
    // Reasoning content (Fable 5 / always-on thinking): visible while partial (thinking
    // indicator, all users) or, once complete, to admins only (WEBAPP-1D follow-up).
    if (block.reasoningContent && (block.partial || isAdmin)) return true;
    // Citation (RAG/grounding) and generated-image blocks: admin-only visibility.
    if (isAdmin && (block.citation || block.image)) return true;
    // Tool use is only visible if showAdvancedFeatures is true, or if it's a TOOL_SEARCH with IDs,
    // or if it's an analyst tool (always show progress label to all users)
    if (block.toolUse) {
      if (showAdvancedFeatures) return true;
      // TOOL_SEARCH may have search button even when showAdvancedFeatures is false
      if (block.toolUse.name === "search" && block.toolUse.input?.ids?.length) return true;
      // Analyst tools always show a compact progress label
      if (ANALYST_TOOLS.has(block.toolUse.name)) return true;
      // run_agent dispatches a long-running flavor agent (WEBAPP-B0/B1: this turn was
      // being silently dropped, hiding ContentBlock's inline flavor-agent progress view
      // for minutes at a time with no feedback)
      if (block.toolUse.name === TOOL_RUN_AGENT) return true;
    }
    return false;
  });

  // Don't render turn if no visible content and no attachments
  if (!hasVisibleContent && attachments.length === 0) {
    return null;
  }

  const contentBlocks = nonFileContent.map((contentBlock, blockNumber) => (
    <ContentBlock key={blockNumber}
                  role={role} agent={agent}
                  block={contentBlock}
                  blockNumber={blockNumber}
                  turnNumber={turnNumber}
                  timestamp={timestamp}
                  turnHasToolUse={content?.some(b => b.toolUse)}
                  messageError={error}
                  truncated={stopReason === "max_tokens"}
                  tokenUsage={tokenUsage}
                  interrupted={stopReason === "user_interrupt"}
                  trapErrors={trapErrors}
                  agentId={agentId}
                  toolInfo={toolUseLookup && (toolUseLookup[contentBlock?.toolResult?.toolUseId] || toolUseLookup[contentBlock?.toolUse?.toolUseId])}
                  showAdvancedFeatures={showAdvancedFeatures}
                  showDetails={showDetails}
                  resolveSourceLinks={resolveSourceLinks}
                  replayConversation={(role === ROLE_USER || hasToolUse) && replayConversation}
                  discardConversation={discardConversation}
                  busy={busy}
                  isSearchProgress={isSearchProgress}
                  searchInfoText={isSearchProgress ? searchLastInfo : undefined}
                  userStore={userStore}
                  isStickyPrompt={isStickyPrompt}
                  isCurrentlySticky={isStickyPrompt && !expanded}
                  isTruncated={isTruncated}
                  expanded={expanded}
                  onVisibilityChange={onVisibilityChange}
                  onToggleExpand={handleToggleExpand}
                  mentionedGenes={mentionedGenes}
                  contentRef={blockNumber === 0 ? contentRef : null}
                  renderControlsAtTurnLevel={!isStickyPrompt}
                  editable={editable}
                  existingAttachments={existingAttachmentFiles}
                  onEdit={onEdit}
                  onSave={onSave}
                  onCancel={onCancel}
    />
  ));

  // Render turn-level controls (replay button) for non-sticky prompts
  // These appear at the bottom-right of the entire turn, not per content-block
  // Only show for: USER prompts (not tool results) or ASSISTANT messages with tool use
  const hasToolResult = content.some(block => block.toolResult);
  const isUserPrompt = role === ROLE_USER && !hasToolResult;
  const isReplayable = isUserPrompt || (role === ROLE_ASSISTANT && hasToolUse);
  const shouldShowControls = isReplayable || (role === ROLE_ASSISTANT && discardConversation);
  const turnLevelControls = !isStickyPrompt && shouldShowControls && !partial && (replayConversation || discardConversation) && (
    <div className={"turn-controls"}>
      {role === ROLE_USER && replayConversation && onEdit && (<div
        key={`edit`}
        className={`glyphicon glyphicon-pencil flat-button${busy ? " disabled" : ""}`}
        title={"Edit this prompt"}
        onClick={e => !busy && onEdit()}
      ></div>)}
      {isReplayable && replayConversation && (<div
        key={`replay`}
        className={`glyphicon glyphicon-refresh flat-button${busy ? " disabled" : ""}`}
        title={`${role === ROLE_USER && !discardConversation ? "Continue" : "Regenerate"} the conversation from this point`}
        onClick={e => replayConversation(turnNumber)}
      ></div>)}
      {discardConversation && (<div
        key={`discard`}
        className={`glyphicon glyphicon-trash flat-button${busy ? " disabled" : ""}`}
        title={"Discard conversation from this point"}
        onClick={e => !busy && discardConversation(turnNumber)}
      ></div>)}
    </div>
  );

  const editingClass = editable ? " editing" : "";

  return (
    <div
      ref={turnRef}
      className={`turn turn-${turnNumber} ${role}${toolClass}${errorClass}${partialClass}${stickyClass}${editingClass}`}
      style={isStickyPrompt ? { zIndex: 10 + turnNumber } : undefined}
    >
      {contentBlocks}
      {attachments}
      {turnLevelControls}
    </div>
  );
});
